import { ANALYTICS } from 'analytics/constants';
import { Formik, FormikConfig, FormikHelpers, FormikValues } from 'formik';
import { useAnalytics } from 'hooks/useAnalytics';
import { DEFAULT_ERROR_MESSAGE, QUESTION_FORM } from './constants';
import { AnalyticFormErrorDetailsType, AnalyticFormSubmittedDetailsType } from 'analytics/types';

function FormWrapper<Values extends FormikValues = FormikValues, ExtraProps = object>(
  props: FormikConfig<Values> &
    ExtraProps &
    Required<{ id: string }> & {
      onError?: (err: Error, helpers: FormikHelpers<Values>) => void;
    },
): React.JSX.Element {
  const { trackEvent } = useAnalytics();
  const { onSubmit, onError, id } = props;

  return (
    <Formik
      {...props}
      onSubmit={(values, helpers) => {
        const handleFormError = (err: unknown) => {
          const error = err as Error;
          trackEvent({
            analyticEvent: ANALYTICS.EVENT.FORM_FAILED,
            details: {
              analytic_form_id: id,
              error_message: error.message,
            } as AnalyticFormErrorDetailsType,
          });
          if (onError) {
            error.message = error.message || DEFAULT_ERROR_MESSAGE;
            onError(error, helpers);
          }
        };

        // we explicitly do not want to track submission of custom question input
        if (id !== QUESTION_FORM) {
          trackEvent({
            analyticEvent: ANALYTICS.EVENT.FORM_SUBMITTED,
            details: {
              analytic_form_id: id,
            } as AnalyticFormSubmittedDetailsType,
          });
        }

        try {
          if (onSubmit) {
            const result = onSubmit(values, helpers);

            // result could be a Promise, so need to catch here as well
            if (result) result.catch(handleFormError);
          }
        } catch (err) {
          // Otherwise any failures should be caught here
          handleFormError(err);
        }
      }}
    />
  );
}

export default FormWrapper;
